import React from "react";
import { Link } from "react-router-dom";
import { Shield, Mail } from "lucide-react";

const LAST_UPDATED = "February 24, 2026";
const CONTACT_EMAIL = "privacy@umaang.co";
const COMPANY = "Umaang Matrimony Services";
const JURISDICTION = "India";

function Section({ n, title, children }) {
  return (
    <section data-testid={`privacy-section-${n}`} className="mt-10">
      <h2 className="font-display text-2xl text-[#1F2937] flex items-baseline gap-3">
        <span className="text-[#C85A53] font-mono text-lg">{String(n).padStart(2, "0")}</span>
        {title}
      </h2>
      <div className="mt-3 text-[#374151] leading-relaxed space-y-3 text-[15px]">
        {children}
      </div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-14" data-testid="privacy-page">
      <Link to="/" data-testid="privacy-back-home" className="text-xs uppercase tracking-widest text-[#C85A53] hover:underline">← Back to Umaang</Link>

      <div className="mt-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl grid place-items-center bg-[#EEF2FF] text-[#4F46E5]">
          <Shield className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-display text-4xl">Privacy Policy</h1>
          <p className="text-xs text-[#57534E] mt-1">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      <div className="mt-8 p-4 rounded-xl bg-gradient-to-r from-[#FEF3C7] to-[#FCE7F3] border border-[#F5D0A9]">
        <p className="text-sm text-[#78350F]">
          <b>Summary in plain Hindi/English:</b> Umaang aapka data sirf shaadi-related matching, verification, aur safety ke liye use karta hai. Aapki Aadhaar/PAN details Digio (RBI-regulated) ke through verify hoti hain, humare paas raw copy nahi rehti. Aap kabhi bhi apna account delete kar sakte ho — hum sab data 30 din me erase kar denge.
        </p>
      </div>

      <Section n={1} title="Who we are">
        <p>
          Umaang (<i>"we", "us", "our"</i>) is a matrimony platform operated by {COMPANY}, incorporated in {JURISDICTION}. This policy explains how we collect, use, share, and safeguard your personal information when you use <b>www.umaang.co</b> or the Umaang mobile app (together the "Service").
        </p>
        <p>By using the Service you agree to this policy. If you do not agree, please do not create an account.</p>
      </Section>

      <Section n={2} title="Information we collect">
        <p><b>A. You give us directly:</b></p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Registration details — name, gender, date of birth, email, mobile number, password.</li>
          <li>Profile details — photos (up to 10), height, weight, religion, caste, mother tongue, education, profession, income range, family details, horoscope information, dietary preferences, location and PIN code.</li>
          <li>Partner preferences — age range, height range, religion, caste, education, location and other filters you set.</li>
          <li>KYC identifiers — Aadhaar number and PAN number are submitted to our KYC partner <b>Digio</b> (RBI/UIDAI licensed AUA); we store only the verification status, timestamp, and a masked reference — <b>not</b> the raw Aadhaar/PAN copy.</li>
          <li>Payment identifiers — Razorpay reference IDs. Card numbers, UPI IDs, CVVs are handled by Razorpay directly and are never stored on our servers.</li>
          <li>Communications — messages you send other members, connection requests, blocks, reports, referral codes shared.</li>
        </ul>
        <p><b>B. Collected automatically:</b></p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Device information — IP address, device model, OS version, browser, app version.</li>
          <li>Usage data — pages viewed, features used, timestamps, crash logs.</li>
          <li>Approximate location — city / PIN code you provide, and (with your permission) precise geolocation for the "Near me" feature. You can revoke permission any time.</li>
          <li>Cookies and similar technologies — session tokens (kept in browser storage) and analytics cookies.</li>
        </ul>
        <p><b>C. From third parties:</b></p>
        <ul className="list-disc pl-6 space-y-1">
          <li>If you sign in with Google, we receive your Google email, name, and profile picture.</li>
          <li>Digio returns KYC pass/fail signals after verifying Aadhaar/PAN.</li>
          <li>Razorpay returns payment success/failure and settlement identifiers.</li>
        </ul>
      </Section>

      <Section n={3} title="How we use your information">
        <ul className="list-disc pl-6 space-y-1">
          <li>To create your profile and show it to compatible prospects (opposite gender, matching preferences).</li>
          <li>To calculate a match percentage and, on request, a Guna Milan (horoscope) score.</li>
          <li>To verify identity through Aadhaar/PAN and reduce fake profiles.</li>
          <li>To send OTPs and transactional notifications by SMS, email, and web push.</li>
          <li>To process Premium subscription payments and Refer-and-Earn rewards.</li>
          <li>To keep the Service safe — detect abuse, spam, harassment, impersonation, fraud.</li>
          <li>To comply with applicable law, court orders, or requests from law enforcement.</li>
          <li>To improve the Service through anonymised analytics.</li>
        </ul>
        <p>We do <b>not</b> sell your personal data. We do <b>not</b> use your photos or Aadhaar for advertising or model training.</p>
      </Section>

      <Section n={4} title="Sharing with third parties">
        <p>Your data is shared only with the trusted service providers necessary to operate the Service:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><b>Digio</b> — Aadhaar/PAN identity verification.</li>
          <li><b>Razorpay</b> — payment processing for Premium plans.</li>
          <li><b>Twilio</b> — SMS OTP delivery.</li>
          <li><b>Resend</b> — transactional email delivery.</li>
          <li><b>Google</b> — optional social sign-in and reCAPTCHA anti-bot.</li>
          <li><b>Cloud hosting (Emergent / MongoDB Atlas)</b> — encrypted storage and compute.</li>
        </ul>
        <p>Other Umaang members see only what your privacy settings allow — for example if you have "Hide name" enabled, prospects see your Member ID (USG####) instead of your full name until you accept their request.</p>
        <p>We may disclose information if required by law, to protect rights and safety, or in a business transfer (with notice to you and this policy continuing to apply).</p>
      </Section>

      <Section n={5} title="Your controls & rights">
        <p>You can, at any time from the Umaang app:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><b>Edit or delete</b> any profile field, photo, preference, or block list.</li>
          <li><b>Hide your name</b> until you accept a request — go to Profile → Privacy toggle.</li>
          <li><b>Revoke geolocation</b> permission from your browser/device settings.</li>
          <li><b>Download your data</b> — email {CONTACT_EMAIL} and we will share a JSON export within 15 working days.</li>
          <li><b>Delete your account</b> — Profile → Settings → Delete account. All personal data is erased within 30 days; anonymised analytics may be retained. Payment records are retained as required by Indian tax law (7 years).</li>
        </ul>
        <p>Under India's Digital Personal Data Protection Act (DPDP Act, 2023) you have the right to access, correct, and erase your personal data, and to nominate someone to exercise these rights in the event of your death or incapacity. Write to our Grievance Officer at {CONTACT_EMAIL} to exercise any right — we will respond within 15 days.</p>
      </Section>

      <Section n={6} title="Security">
        <p>We use TLS 1.2+ for all data in transit, bcrypt for passwords, encrypted storage for photos, and role-based access controls internally. However, no system is 100% secure — please never share your password, and use a unique one you don't reuse elsewhere.</p>
        <p>If you suspect a security incident, email {CONTACT_EMAIL} immediately.</p>
      </Section>

      <Section n={7} title="Retention">
        <p>Active account data is retained while your account exists. On deletion, personal data is erased within 30 days. The following are retained longer where legally required: payment invoices (7 years, Indian tax law), KYC audit trails (5 years, RBI/UIDAI guidelines), abuse/fraud logs (2 years for platform safety).</p>
      </Section>

      <Section n={8} title="Children">
        <p>Umaang is strictly for adults 18 years or older intending to marry. We do not knowingly onboard minors. If we discover an underage account, we delete it immediately. Parents/guardians can email {CONTACT_EMAIL} to report a suspected underage profile.</p>
      </Section>

      <Section n={9} title="International transfers">
        <p>Our servers are located in India. If you access the Service from outside India, your data will be transferred to and processed in India. By using Umaang from outside India, you consent to this transfer.</p>
      </Section>

      <Section n={10} title="Changes to this policy">
        <p>We may update this policy from time to time. We will notify you of material changes by email or in-app banner at least 15 days before they take effect. Continued use after the effective date constitutes acceptance.</p>
      </Section>

      <Section n={11} title="Grievance Officer">
        <p>Under Rule 3(11) of the IT (Intermediary Guidelines) Rules 2021 and the DPDP Act 2023, our Grievance Officer is:</p>
        <div className="p-4 bg-[#F9FAFB] border border-[#D6D2C9] rounded-xl">
          <p className="text-sm"><b>Grievance Officer, Umaang</b></p>
          <p className="text-sm flex items-center gap-2 mt-1"><Mail className="w-3.5 h-3.5" /> {CONTACT_EMAIL}</p>
          <p className="text-sm mt-1">Response time: within 15 working days.</p>
        </div>
      </Section>

      <Section n={12} title="Contact">
        <p>Questions or feedback? Reach us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#C85A53] hover:underline">{CONTACT_EMAIL}</a>.</p>
      </Section>

      <div className="mt-16 pt-6 border-t border-[#D6D2C9] flex items-center justify-between text-xs text-[#57534E]">
        <span>© {new Date().getFullYear()} {COMPANY}</span>
        <div className="flex gap-4">
          <Link to="/terms" data-testid="privacy-to-terms" className="hover:underline">Terms of Service</Link>
          <Link to="/" className="hover:underline">Home</Link>
        </div>
      </div>
    </div>
  );
}
